import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { Field, Select } from "@/components/Form";
import { Spinner } from "@/components/Loader";
import ShareCardPreview from "@/components/ShareCardPreview";
import { affGet } from "@/lib/affiliateApi";
import { toast } from "@/components/Toast";
import { fmtMoney, titleCase } from "@/lib/format";
import { C } from "@/lib/brand";

// Affiliate dashboard home: KPIs, primary link, UTM builder, recent activity.
// Polls /api/affiliate/dashboard every 60s while the tab is active.
export default function Overview() {
  const { profile } = useOutletContext();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchDash = async () => {
      try {
        const data = await affGet("/affiliate/dashboard", { auth: true });
        if (!cancelled) {
          setDashboard(data || {});
          setError(false);
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDash();

    const interval = setInterval(() => {
      if (!document.hidden) fetchDash();
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div data-testid="overview-loading" style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={32} />
      </div>
    );
  }

  const stats = dashboard?.thisMonth || {};
  const totals = dashboard?.totals || {};
  const nextPayout = dashboard?.nextPayout;
  const activity = dashboard?.recentActivity || [];
  const baseLink = profile?.affiliateLink || `https://voya9.com/?aff=${profile?.affiliateCode || "your-code"}`;

  return (
    <div data-testid="affiliate-overview" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ DASHBOARD</Eyebrow>
      <Headline before="HELLO," italic={`${titleCase(profile?.firstName || "there")}.`} size={48} className="mt-4" />

      {error && (
        <div data-testid="overview-error" style={{ marginTop: 22, padding: "14px 16px", background: C.bone, borderLeft: `3px solid ${C.coral}`, fontSize: 13, color: C.warmGray }}>
          Live performance data is temporarily unavailable. Showing what we have.
        </div>
      )}

      {/* KPI cards */}
      <section data-testid="overview-kpis" style={{ marginTop: 36 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 16 }}>THIS MONTH</div>
        <div
          style={{ display: "grid", gap: 12 }}
          className="grid-cols-2 md:!grid-cols-4"
        >
          <KpiCard testId="kpi-clicks" label="CLICKS" value={fmtNum(stats.clicks)} delta={stats.clicksDelta} />
          <KpiCard testId="kpi-conversions" label="CONVERSIONS" value={fmtNum(stats.conversions)} delta={stats.conversionsDelta} />
          <KpiCard testId="kpi-month-earn" label="THIS MONTH" value={fmtMoney(stats.earnings || 0)} sub="Available" />
          <KpiCard testId="kpi-total-earn" label="TOTAL EARNED" value={fmtMoney(totals.lifetimeEarnings || 0)} sub="Lifetime" />
        </div>
      </section>

      {nextPayout && (
        <section data-testid="overview-next-payout" style={{ marginTop: 32, padding: 24, background: C.peach, color: C.peachDark }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDark }}>★ NEXT PAYOUT</div>
          <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
            {fmtMoney(nextPayout.amount || 0)}
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: C.peachDark }}>
            Scheduled for {nextPayout.scheduledFor || "the 15th"} via {nextPayout.method || "your payout method"}
          </div>
        </section>
      )}

      {/* Your link + UTM builder */}
      <section data-testid="overview-links" style={{ marginTop: 48 }}>
        <Eyebrow>★ YOUR LINKS</Eyebrow>
        <Headline before="SHARE &" italic="track." size={40} className="mt-3" />

        <div
          style={{ marginTop: 24, display: "grid", gap: 24 }}
          className="grid-cols-1 md:grid-cols-2"
        >
          <LinkBuilder profile={profile} baseLink={baseLink} />
          <SharePreview baseLink={baseLink} />
        </div>
      </section>

      {/* Recent activity */}
      <section data-testid="overview-activity" style={{ marginTop: 48 }}>
        <Eyebrow>★ RECENT ACTIVITY</Eyebrow>
        <Headline before="THE LAST" italic="few weeks." size={40} className="mt-3" />

        <div style={{ marginTop: 24, background: C.pure, border: `1px solid ${C.warmGray3}` }}>
          {activity.length === 0 ? (
            <div data-testid="activity-empty" style={{ padding: 28, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              No activity yet. Your link tracks every click — share it once and watch this fill up.
            </div>
          ) : (
            activity.slice(0, 8).map((row, i) => <ActivityRow key={i} row={row} />)
          )}
        </div>
      </section>
    </div>
  );
}

/* ===== KPI card ===== */
function KpiCard({ testId, label, value, delta, sub }) {
  return (
    <div data-testid={testId} style={{ padding: 20, background: C.bone, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>{label}</div>
      <div style={{ marginTop: 10, fontSize: 26, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em", overflowWrap: "break-word" }}>
        {value}
      </div>
      {delta != null && (
        <div style={{ marginTop: 4, fontSize: 11, color: delta >= 0 ? C.sageDark : C.coral, fontWeight: 700 }}>
          {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}% mom
        </div>
      )}
      {sub && <div style={{ marginTop: 4, fontSize: 11, color: C.warmGray }}>{sub}</div>}
    </div>
  );
}

/* ===== Link builder (primary link + UTM) ===== */
function LinkBuilder({ profile, baseLink }) {
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");
  const [content, setContent] = useState("");

  const trackedLink = useMemo(() => {
    const url = new URL(baseLink, window.location.origin);
    if (source) url.searchParams.set("utm_source", source);
    if (campaign) url.searchParams.set("utm_campaign", campaign);
    if (content) url.searchParams.set("utm_content", content);
    return url.toString();
  }, [baseLink, source, campaign, content]);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>PRIMARY LINK</div>
      <CopyableLink testId="primary-link" url={baseLink} />

      <div style={{ marginTop: 26, padding: 22, background: C.bone, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 8 }}>
          BUILD A TRACKED LINK
        </div>
        <Select
          label="SOURCE"
          value={source}
          onChange={setSource}
          options={["instagram", "tiktok", "youtube", "podcast", "newsletter", "twitter", "linkedin", "blog", "other"]}
          testId="utm-source"
        />
        <Field label="CAMPAIGN" value={campaign} onChange={setCampaign} placeholder="spring2026" testId="utm-campaign" />
        <Field label="CONTENT" value={content} onChange={setContent} placeholder="story_post_1" testId="utm-content" />
        <div style={{ marginTop: 16 }}>
          <CopyableLink testId="tracked-link" url={trackedLink} compact />
        </div>
      </div>
    </div>
  );
}

/* ===== Share preview (right rail) ===== */
function SharePreview({ baseLink }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 12 }}>
        WHAT YOUR AUDIENCE SEES
      </div>
      <ShareCardPreview
        url={baseLink}
        title="Feel like you again."
        description="VOYA9 — physician-supervised wellness programs built around your body, your life."
        accent="peach"
        testId="share-card-overview"
      />
      <p style={{ marginTop: 10, fontSize: 12, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
        Live preview of your share card across Instagram, X, LinkedIn, and Messages.
      </p>
    </div>
  );
}

/* ===== Copyable link with QR ===== */
function CopyableLink({ url, testId, compact = false }) {
  const [showQR, setShowQR] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast("Link copied.", "success");
    } catch {
      toast("Could not copy. Long-press to select.", "error");
    }
  };

  return (
    <div data-testid={testId} style={{ marginTop: compact ? 0 : 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
        <input
          readOnly
          value={url}
          data-testid={`${testId}-input`}
          onFocus={(e) => e.target.select()}
          style={{
            flex: 1,
            minWidth: 0,
            padding: "12px 14px",
            background: C.pure,
            border: `1.5px solid ${C.warmGray3}`,
            fontSize: 13,
            color: C.ink,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
          }}
        />
        <button
          type="button"
          data-testid={`${testId}-copy`}
          onClick={copy}
          style={{
            padding: "0 18px",
            background: C.ink,
            color: C.cream,
            border: 0,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.18em",
            cursor: "pointer",
          }}
        >
          COPY
        </button>
        <button
          type="button"
          data-testid={`${testId}-qr`}
          onClick={() => setShowQR((s) => !s)}
          style={{
            padding: "0 14px",
            background: showQR ? C.peachDeep : "transparent",
            color: showQR ? C.cream : C.ink,
            border: `1.5px solid ${C.ink}`,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.18em",
            cursor: "pointer",
          }}
        >
          QR
        </button>
      </div>
      {showQR && (
        <div data-testid={`${testId}-qr-code`} style={{ marginTop: 14, padding: 18, background: C.pure, border: `1px solid ${C.warmGray3}`, display: "inline-block" }}>
          <QRCodeSVG value={url} size={148} bgColor={C.pure} fgColor={C.ink} level="M" />
        </div>
      )}
    </div>
  );
}

/* ===== Activity row ===== */
function ActivityRow({ row }) {
  const isConv = row.type === "conversion";
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        alignItems: "center",
        padding: "14px 18px",
        borderBottom: `1px solid ${C.warmGray3}`,
        fontSize: 14,
      }}
    >
      <div style={{ width: 64, fontSize: 11, fontWeight: 800, letterSpacing: "0.16em", color: C.warmGray }}>
        {row.date || "—"}
      </div>
      <div style={{ flex: 1, minWidth: 0, color: C.ink, lineHeight: 1.45 }}>
        {isConv ? (
          <>
            <strong>Conversion</strong> · {row.programName || "VOYA9"} {row.tier ? `· ${row.tier}` : ""}
          </>
        ) : (
          <>{fmtNum(row.clicks || 0)} clicks{row.conversions ? ` · ${row.conversions} conversions` : ""}</>
        )}
      </div>
      {row.amount != null && (
        <div style={{ fontSize: 14, fontWeight: 700, color: isConv ? C.sageDark : C.warmGray, flexShrink: 0 }}>
          +{fmtMoney(row.amount)}
        </div>
      )}
    </div>
  );
}

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1000) return n.toLocaleString();
  return String(n);
}
